import { formatNumberToTime, getDateTime } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import {
  ICorporateCreditsDistributionSchedule,
  ICorporateCreditsDistributionScheduleFilterParams,
} from '../../../pages/corporates-container/share-type'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import YesOrNoTag from '../../common/YesOrNoTag'
import LinkButton from '../../link-button/link-button'
import { SearchOutlined } from '@ant-design/icons'
import { Button, Typography, Form, Row, Select, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useEffect } from 'react'

const { Option } = Select
const { Title } = Typography

interface CorporateCreditsDistributionScheduleTableProps {
  schedules: ICorporateCreditsDistributionSchedule[]
  corporateId: number
  pageInfo: PageInfo
  paginationOnChange: (page: number, pageSize: number) => void
  onSearch: (data: ICorporateCreditsDistributionScheduleFilterParams) => void
  searchValues: ICorporateCreditsDistributionScheduleFilterParams | undefined
}

const CorporateCreditsDistributionScheduleTable = ({
  schedules,
  corporateId,
  pageInfo,
  paginationOnChange,
  onSearch,
  searchValues,
}: CorporateCreditsDistributionScheduleTableProps) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (searchValues) {
      form.setFieldsValue({
        searchValues,
      })
    }
  }, [searchValues])

  const onFinish = (values: ICorporateCreditsDistributionScheduleFilterParams) => {
    onSearch(values)
  }

  const columns: ColumnsType<ICorporateCreditsDistributionSchedule> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      fixed: 'left',
      width: 60,
    },
    {
      title: translate('isActive'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) => <YesOrNoTag value={isActive} />,
    },
    {
      title: translate('corporate.corporateCreditsDistributionSchedule.modal.isApplyForAllMembers'),
      dataIndex: 'isApplyForAllMembers',
      key: 'isApplyForAllMembers',
      width: 100,
      render: (isActive: boolean) => <YesOrNoTag value={isActive} />,
    },
    {
      title: translate('corporate.corporateCreditsDistributionSchedule.modal.schedule.type'),
      dataIndex: ['schedule', 'type'],
      key: 'type',
      width: 120,
    },
    {
      title: translate('corporate.credits'),
      dataIndex: 'credits',
      key: 'credits',
      width: 100,
    },
    {
      title: translate('corporate.bufferType'),
      dataIndex: 'bufferType',
      key: 'bufferType',
      width: 100,
    },
    {
      title: translate('corporate.bufferFixedValue'),
      dataIndex: 'bufferFixedValue',
      key: 'bufferFixedValue',
      width: 100,
    },
    {
      title: translate('corporate.bufferPercentage'),
      dataIndex: 'bufferPercentage',
      key: 'bufferPercentage',
      width: 120,
    },
    {
      title: translate('corporate.corporateCreditsDistributionSchedule.modal.isSendSms'),
      dataIndex: 'isSendSms',
      key: 'isSendSms',
      width: 100,
      render: (isSendSms: boolean) => <YesOrNoTag value={isSendSms} />,
    },
    {
      title: translate('corporate.corporateCreditsDistributionSchedule.modal.smsSendTime'),
      dataIndex: 'smsSendTime',
      key: 'smsSendTime',
      width: 100,
      render: (smsSendTime?: number) => (smsSendTime ? formatNumberToTime(smsSendTime) : '-'),
    },
    {
      title: translate('createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt: string) => (createdAt ? getDateTime(createdAt) : ''),
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (text: string, record: ICorporateCreditsDistributionSchedule) => (
        <Space>
          <AuthPermission
            permission={PermissionsEnum.CORPORATE_CREDITS_DISTRIBUTION_SCHEDULES_WRITE}
          >
            <LinkButton
              to={`/corporates/${corporateId}/credits-distribution-schedules/${record.id}/edit`}
            >
              {translate('edit')}
            </LinkButton>
          </AuthPermission>
          <AuthPermission
            permission={PermissionsEnum.CORPORATE_CREDITS_DISTRIBUTION_SCHEDULES_READ}
          >
            <LinkButton
              to={`/corporates/${corporateId}/credits-distribution-schedules/${record.id}`}
            >
              {translate('view')}
            </LinkButton>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className="table-space" orientation="vertical" size={16}>
      <Space className="table-space" orientation="vertical" size={16}>
        <Title level={5}>{translate('navbar.corporateCreditsDistributionSchedule')}</Title>

        <Row className="filter-bar">
          <Form form={form} layout="inline" onFinish={onFinish}>
            <Form.Item name="isActive" label="活跃">
              <Select style={{ minWidth: 130 }} allowClear>
                <Option value={true}>是</Option>
                <Option value={false}>否</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" type="primary" icon={<SearchOutlined />}>
                搜索
              </Button>
            </Form.Item>
          </Form>
        </Row>

        <Table
          rowKey={record => record.id}
          columns={columns}
          dataSource={schedules}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageInfo.pageSize,
            total: pageInfo.count,
            onChange: paginationOnChange,
          }}
          scroll={{ x: 1140 }}
        />
      </Space>
    </Space>
  )
}

export default CorporateCreditsDistributionScheduleTable
